import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, Check, Mail, User, MessageSquare, X, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/booking`;

// A minimal public axios that does NOT carry credentials (this page is fully public).
const pub = axios.create({ baseURL: API });

function ymd(d) { return d.toISOString().split("T")[0]; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function labelDate(d) { return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }

export default function PublicCalendarBooking({ cancelMode = false }) {
  const { slug, token } = useParams();
  const [cal, setCal] = useState(null);
  const [error, setError] = useState("");
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [searchParams] = useSearchParams();

  // Cancellation mode: look up appointment via token and show cancel UI
  const [cancelState, setCancelState] = useState({ loading: cancelMode, appt: null, cancelled: false });

  const attendeeTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    []
  );

  // Load calendar info
  useEffect(() => {
    if (cancelMode) return;
    pub.get(`/${slug}`)
      .then(({ data }) => setCal(data))
      .catch((e) => setError(e.response?.data?.detail || "Calendar not found"));
  }, [slug, cancelMode]);

  // Load appointment for cancel mode
  useEffect(() => {
    if (!cancelMode || !token) return;
    pub.get(`/appointments/${token}`)
      .then(({ data }) => setCancelState({ loading: false, appt: data, cancelled: data.status === "cancelled" }))
      .catch(() => setCancelState({ loading: false, appt: null, cancelled: false }));
  }, [cancelMode, token]);

  // Load slots when date changes
  useEffect(() => {
    if (!selectedDate) { setSlots([]); return; }
    setSlotsLoading(true); setSelectedSlot(null);
    pub.get(`/${slug}/slots`, { params: { date: selectedDate } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate, slug]);

  const submitBooking = async () => {
    if (!selectedSlot || !form.name || !form.email) return;
    setSubmitting(true);
    try {
      const { data } = await pub.post(`/${slug}/book`, {
        name: form.name.trim(),
        email: form.email.trim(),
        notes: form.notes,
        start: selectedSlot,
        timezone: attendeeTz,
      });
      setConfirmed(data);
      toast.success("Booking confirmed — check your inbox for details");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed — please try another slot");
    } finally { setSubmitting(false); }
  };

  const doCancel = async () => {
    if (!token) return;
    try {
      await pub.post(`/appointments/${token}/cancel`);
      setCancelState((s) => ({ ...s, cancelled: true }));
      toast.success("Booking cancelled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  // ---------- Cancel view ----------
  if (cancelMode) {
    if (cancelState.loading) return <PublicShell><p className="text-sm text-[var(--peak-muted)]">Loading…</p></PublicShell>;
    if (!cancelState.appt) return <PublicShell><h1 className="font-heading font-bold text-3xl">This link is invalid or expired.</h1></PublicShell>;
    const a = cancelState.appt;
    return (
      <PublicShell>
        <div className="section-label">Cancel booking</div>
        {cancelState.cancelled ? (
          <>
            <h1 className="font-heading font-bold text-3xl mt-2 leading-tight">This booking has been cancelled.</h1>
            <p className="text-sm text-[var(--peak-muted)] mt-4">You&apos;re free — no calendar hold, no confirmation.</p>
          </>
        ) : (
          <>
            <h1 className="font-heading font-bold text-3xl mt-2 leading-tight">Are you sure?</h1>
            <p className="mt-4"><b>{a.calendar_name}</b></p>
            <p className="text-sm">{new Date(a.start).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}</p>
            <p className="text-xs text-[var(--peak-muted)] mt-2">Booked as {a.contact_email}</p>
            <div className="mt-6 flex gap-2">
              <button data-testid="cancel-booking-confirm" onClick={doCancel} className="bg-[#E50000] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 border-2 border-[#E50000] hover:bg-black hover:border-black">Cancel booking</button>
              <a href={`/book/c/${a.calendar_slug}`} className="border-2 border-[var(--peak-ink)] text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-black hover:text-white">Keep it</a>
            </div>
          </>
        )}
      </PublicShell>
    );
  }

  // ---------- Error / loading ----------
  if (error) return <PublicShell><h1 className="font-heading font-bold text-3xl">{error}</h1></PublicShell>;
  if (!cal) return <PublicShell><p className="text-sm text-[var(--peak-muted)]">Loading…</p></PublicShell>;

  // ---------- Confirmed view ----------
  if (confirmed) {
    const start = new Date(confirmed.appointment.start);
    return (
      <PublicShell>
        <div className="section-label flex items-center gap-1"><Check className="w-3 h-3 text-[#008A27]" /> Confirmed</div>
        <h1 className="font-heading font-bold text-3xl mt-2 leading-tight" data-testid="booking-confirmed">You&apos;re booked.</h1>
        <p className="mt-4"><b>{cal.name}</b> · {cal.duration_min} min</p>
        <p className="text-lg font-semibold" data-testid="booking-confirmed-time">
          {start.toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
        </p>
        <p className="text-xs text-[var(--peak-muted)] mt-2">A confirmation is on the way to {confirmed.appointment.contact_email}.</p>
        <a href={confirmed.cancel_url} className="mt-6 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest underline hover:text-[var(--peak-accent)]">
          <ExternalLink className="w-3 h-3" /> Need to cancel? Use this link
        </a>
      </PublicShell>
    );
  }

  // ---------- Booking flow ----------
  return (
    <PublicShell wide>
      <Toaster position="top-center" richColors />
      <div className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr_1fr] gap-0 min-h-[560px]">
        <div className="border-b md:border-b-0 md:border-r border-[var(--peak-ink)] p-6 md:p-8 bg-[var(--peak-surface-alt)]">
          <div className="section-label">{cal.owner_name || cal.owner_email}</div>
          <h1 className="font-heading font-bold text-3xl leading-none mt-2 tracking-tight" data-testid="booking-cal-name">{cal.name}</h1>
          {cal.description && <p className="text-sm text-[var(--peak-muted)] mt-4">{cal.description}</p>}
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {cal.duration_min} min</div>
            <div className="flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Times in <b>{attendeeTz}</b></div>
          </div>
        </div>

        <div className="border-b md:border-b-0 md:border-r border-[var(--peak-ink)] p-6" data-testid="booking-month-view">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1 hover:bg-black hover:text-white" data-testid="month-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="section-label">{cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1 hover:bg-black hover:text-white" data-testid="month-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <MonthGrid cursor={cursor} availability={cal.availability} selectedDate={selectedDate} onPick={setSelectedDate} />
        </div>

        <div className="p-6" data-testid="booking-right-pane">
          {!selectedDate ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-sm text-[var(--peak-muted)]">Pick a date to see open slots.</p>
            </div>
          ) : !selectedSlot ? (
            <>
              <div className="section-label">{labelDate(new Date(selectedDate + "T00:00:00"))}</div>
              {slotsLoading ? (
                <p className="text-sm text-[var(--peak-muted)] mt-4">Loading slots…</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-[var(--peak-muted)] mt-4">No open slots on this day. Try another.</p>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2 max-h-[440px] overflow-y-auto">
                  {slots.map((iso) => {
                    const d = new Date(iso);
                    const label = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                    return (
                      <button
                        key={iso}
                        onClick={() => setSelectedSlot(iso)}
                        data-testid={`slot-${iso}`}
                        className="border-2 border-[var(--peak-ink)] text-sm font-bold px-3 py-2 hover:bg-black hover:text-white"
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <button onClick={() => setSelectedSlot(null)} className="section-label inline-flex items-center gap-1 mb-3 hover:text-[var(--peak-accent)]" data-testid="booking-back-to-slots">
                <ChevronLeft className="w-3 h-3" /> Change time
              </button>
              <div className="text-lg font-bold">
                {new Date(selectedSlot).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
              <div className="mt-4 space-y-3">
                <FormField label="Name" icon={User}>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" className="brutalist-input" placeholder="Alice Smith" />
                </FormField>
                <FormField label="Email" icon={Mail}>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" className="brutalist-input" placeholder="alice@company.com" />
                </FormField>
                <FormField label="Notes (optional)" icon={MessageSquare}>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" className="brutalist-input min-h-[80px]" placeholder="Anything you want us to prep?" />
                </FormField>
                <button
                  onClick={submitBooking}
                  disabled={!form.name || !form.email || submitting}
                  data-testid="booking-submit"
                  className="w-full bg-[var(--peak-accent)] text-white text-xs font-bold uppercase tracking-widest px-4 py-3 border-2 border-[var(--peak-accent)] hover:bg-black hover:border-black disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Booking…" : "Confirm booking"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </PublicShell>
  );
}

function PublicShell({ children, wide = false }) {
  return (
    <div className="min-h-screen bg-[#E8E8E4] flex items-start justify-center py-10 px-4">
      <div className={`w-full ${wide ? "max-w-5xl" : "max-w-lg"} bg-white border-2 border-[var(--peak-ink)] p-8 md:p-0`}>
        <div className={wide ? "" : "p-8"}>
          {children}
        </div>
      </div>
    </div>
  );
}

function FormField({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="section-label inline-flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      {children}
    </label>
  );
}

function MonthGrid({ cursor, availability, selectedDate, onPick }) {
  const first = startOfMonth(cursor);
  const dim = daysInMonth(cursor);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return (
    <>
      <div className="grid grid-cols-7 gap-1 text-[10px] font-mono text-[var(--peak-muted)] mb-1">
        {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((d) => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const wd = (d.getDay() + 6) % 7;
          const isAvailable = availability?.[String(wd)]?.enabled;
          const isPast = d < today;
          const ymdKey = ymd(d);
          const isSelected = selectedDate === ymdKey;
          const clickable = isAvailable && !isPast;
          return (
            <button
              key={i}
              disabled={!clickable}
              data-testid={clickable ? `day-${ymdKey}` : undefined}
              onClick={() => clickable && onPick(ymdKey)}
              className={`aspect-square text-sm font-mono border ${
                isSelected ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]"
                : clickable ? "border-[var(--peak-border)] hover:border-[var(--peak-ink)] hover:bg-[var(--peak-surface-alt)]"
                : "border-transparent text-[#B8B8B4] cursor-default"
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </>
  );
}
